import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard, Pill, StatePill } from "../components/Ui";
import StateTimeline from "../components/StateTimeline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth, hasPermission, hasRole, isAdmin } from "../context/AuthContext";
import PurchaseLabComparison from "../components/lab/PurchaseLabComparison";
import { useWS } from "../context/WebSocketContext";
import PurchaseDetailLinks from "../components/PurchaseDetailLinks";
import AdminWeighCorrection from "../components/AdminWeighCorrection";
import AdminEntityActions from "../components/AdminEntityActions";
import AccountsLinkedContractsEditor from "../components/AccountsLinkedContractsEditor";
import WeighEvidencePanel from "../components/WeighEvidencePanel";
import { canWeighCorrectPurchase } from "../lib/unloadingHelpers";
import { labPageUrl } from "../lib/workflowLinks";

export default function PurchaseDetailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [p, setP] = useState(null);
  const [negForm, setNegForm] = useState({ revised_rate: "", weight_cut_pct: "", price_cut_pct: "" });
  const [deductionScope, setDeductionScope] = useState("COMPLETE");
  const [linkEditOpen, setLinkEditOpen] = useState(() => searchParams.get("editLinks") === "1");
  const [linkSaving, setLinkSaving] = useState(false);
  const linkEditorRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/purchases/${id}`).then((r) => {
      setP(r.data);
      setNegForm((f) => ({ ...f, revised_rate: r.data.negotiated_rate }));
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const { subscribe } = useWS();
  useEffect(() => subscribe("purchase", load), [subscribe, load]);

  const closeLinkEdit = useCallback(() => {
    setLinkEditOpen(false);
    if (searchParams.get("editLinks") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("editLinks");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (!p) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const showLabLimits = isAdmin(user);
  const canSeeLabVerdict = isAdmin(user) || hasRole(user, "manager");
  const purchaseLabLimits = showLabLimits ? p.lab_limits : null;

  const decide = async (decision) => {
    try {
      await api.post("/purchases/negotiate", {
        purchase_id: id,
        revised_rate: +negForm.revised_rate || null,
        weight_cut_pct: +negForm.weight_cut_pct || 0,
        price_cut_pct: +negForm.price_cut_pct || 0,
        decision,
      });
      toast.success(`Decision: ${decision}`);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const decideResume = async () => {
    try {
      await api.post("/purchases/negotiate", {
        purchase_id: id,
        revised_rate: +negForm.revised_rate || null,
        weight_cut_pct: +negForm.weight_cut_pct || 0,
        price_cut_pct: +negForm.price_cut_pct || 0,
        decision: "APPROVE",
        resume_unloading: true,
      });
      toast.success("Approved with cuts — unloading resumed");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const weigh = async (kind) => {
    try {
      await api.post("/purchases/weigh", { purchase_id: id, kind });
      toast.success(
        kind === "tare" ? "Tare recorded — sent to accounts for final calculation" : `${kind.toUpperCase()} captured`,
      );
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const accountsFinalize = async () => {
    try {
      await api.post(`/purchases/${id}/accounts-finalize`, {
        revised_rate: negForm.revised_rate !== "" ? +negForm.revised_rate : undefined,
        weight_cut_pct: +negForm.weight_cut_pct || 0,
        price_cut_pct: +negForm.price_cut_pct || 0,
        deduction_scope: deductionScope,
      });
      toast.success("Accounts finalized — voucher(s) generated");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const canAccountsFinalize = hasPermission(user, "purchases.accounts_finalize");
  const unloadedQtl = Number(p?.unloading?.total_weight_qtl || 0);
  const netQtl = Number(p?.net_weight_qtl || 0);
  const isPartialUnload = unloadedQtl > 0 && netQtl > 0 && unloadedQtl < netQtl;
  const canAdminEditPurchaseLinks =
    isAdmin(user) && p && p.state !== "REJECTED" && (p.contract_allocations?.length || p.party_id);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="purchase-detail">
      <PageHeader
        eyebrow={`${p.code}`}
        title={`${p.party_name} · ${p.variety}`}
        description={
          p.source_farmer_name
            ? `Gate pass ${p.gate_pass_code} · ${p.bags_reported} bags reported · Gate farmer ${p.source_farmer_name}`
            : `Gate pass ${p.gate_pass_code} · ${p.bags_reported} bags reported`
        }
        actions={
          <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
            {p.lab_required === false ? (
              <Pill tone="gold">
                {p.lab_verdict === "SAMPLE_NOT_RECEIVED" ? "Truck lab — sample not received" : "Truck lab exempt"}
              </Pill>
            ) : (
              <Pill tone="sage">Truck lab required</Pill>
            )}
            <div className="flex items-center justify-end gap-2">
              <AdminEntityActions
                entityType="purchase"
                entityId={p.id}
                entityLabel={p.code}
                onDeleted={() => navigate("/purchases")}
              />
              <Link to="/purchases" className="text-xs text-muted-foreground hover:underline text-center sm:text-right">← Back to list</Link>
            </div>
          </div>
        }
      />

      <PurchaseDetailLinks purchase={p} />

      {canAdminEditPurchaseLinks && (
        <SectionCard title="Linked purchase contracts" testId="purchase-linked-contracts">
          {!linkEditOpen ? (
            <div className="space-y-3">
              {(p.contract_allocations?.length ?? 0) > 0 ? (
                <ul className="space-y-1 text-sm">
                  {p.contract_allocations.map((a) => (
                    <li key={a.id || a.purchase_contract_id} className="font-mono text-xs">
                      {a.contract_number || a.purchase_contract_id}
                      {a.variety ? ` · ${a.variety}` : ""}
                      {a.net_weight_qtl != null || a.planned_qtl != null
                        ? `: ${a.net_weight_qtl ?? a.planned_qtl} qtl`
                        : ""}
                      {a.rate_per_qtl != null ? ` @ ₹${a.rate_per_qtl}` : ""}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No contracts linked yet.</p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="purchase-edit-links"
                onClick={() => setLinkEditOpen(true)}
              >
                Edit link
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <AccountsLinkedContractsEditor
                ref={linkEditorRef}
                purchase={p}
                canEdit
                onSaved={async () => {
                  await load();
                  closeLinkEdit();
                }}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  disabled={linkSaving}
                  onClick={closeLinkEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="text-xs bg-bansal-sage hover:bg-bansal-sageDark text-white"
                  disabled={linkSaving}
                  data-testid="purchase-save-links"
                  onClick={async () => {
                    try {
                      setLinkSaving(true);
                      await linkEditorRef.current?.save?.();
                    } finally {
                      setLinkSaving(false);
                    }
                  }}
                >
                  {linkSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      <SectionCard title="Transaction Lifecycle" testId="timeline">
        <StateTimeline current={p.state} history={p.history || []} />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Weights">
          <dl className="space-y-3 text-sm">
            <Row label="Gross (on arrival)">{p.gross_weight_qtl ?? "—"} {p.gross_weight_qtl && "qtl"}</Row>
            <Row label="Tare (after unload)">{p.tare_weight_qtl ?? "—"} {p.tare_weight_qtl && "qtl"}</Row>
            <Row label="Net">
              <span className="font-medium text-primary">{p.net_weight_qtl ?? "—"} {p.net_weight_qtl && "qtl"}</span>
            </Row>
            <Row label="Rate"><span className="tabular">₹{p.rate_per_qtl}/qtl</span></Row>
            <Row label="Negotiated"><span className="tabular font-medium">₹{p.negotiated_rate}/qtl</span></Row>
          </dl>
          <div className="flex gap-2 mt-5">
            {p.state === "GATE_IN" && (
              <Button onClick={() => weigh("gross")} className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full" data-testid="capture-gross">Capture Gross</Button>
            )}
            {["UNLOADING", "APPROVED", "WEIGH_TARE"].includes(p.state) && p.gross_weight_qtl && !p.tare_weight_qtl && (
              <Button onClick={() => weigh("tare")} className="bg-bansal-gold hover:bg-bansal-goldDark text-white w-full" data-testid="capture-tare">Capture Tare & Finalize</Button>
            )}
          </div>
          {(isAdmin(user) || hasRole(user, "weighbridge")) && canWeighCorrectPurchase(p) && (
            <div className="mt-4">
              <AdminWeighCorrection purchase={p} onSaved={load} />
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Photos & weigh evidence" testId="purchase-photos">
        <WeighEvidencePanel
          purchaseId={p.id}
          doc={p}
          showGatePhotos
          title="Gate · gross · tare · other linked images"
          testId="vehicle-snapshot"
        />
      </SectionCard>

      {p.lab_comparison ? (
        <PurchaseLabComparison
          purchase={p}
          comparison={p.lab_comparison}
          labLimits={purchaseLabLimits}
        />
      ) : (
        <SectionCard title="Lab analysis">
          <div className="text-sm text-muted-foreground">
            Not yet sampled.{" "}
            {hasPermission(user, "page.lab") ? (
              <Link to={labPageUrl("truck")} className="link-brand">
                Go to truck lab →
              </Link>
            ) : (
              "Lab access required."
            )}
          </div>
        </SectionCard>
      )}

      {p.state === "ACCOUNTS_PENDING" && canAccountsFinalize && (
        <SectionCard title="Accounts — Final Calculation" testId="accounts-finalize-form">
          <div className="bg-sky-50 border border-sky-200 rounded-md p-4 mb-4 text-sm text-sky-900">
            Tare weight is recorded ({p.net_weight_qtl} qtl net). Review cuts and rate, then finalize to generate
            payable voucher(s) and complete this purchase.
          </div>
          {(p.contract_allocations?.length ?? 0) > 0 && (
            <div className="mb-4 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Contract split</div>
              <ul className="space-y-1">
                {p.contract_allocations.map((a) => (
                  <li key={a.id} className="font-mono text-xs">
                    {a.contract_number || a.purchase_contract_id}: {a.net_weight_qtl ?? a.planned_qtl ?? "—"} qtl @ ₹
                    {a.rate_per_qtl}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Final rate (₹/qtl)</Label>
              <Input
                type="number"
                value={negForm.revised_rate}
                onChange={(e) => setNegForm({ ...negForm, revised_rate: e.target.value })}
                className="mt-1.5"
                data-testid="accounts-revised-rate"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Weight cut %</Label>
              <Input
                type="number"
                step="0.1"
                value={negForm.weight_cut_pct}
                onChange={(e) => setNegForm({ ...negForm, weight_cut_pct: e.target.value })}
                className="mt-1.5"
                data-testid="accounts-weight-cut"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Price cut %</Label>
              <Input
                type="number"
                step="0.1"
                value={negForm.price_cut_pct}
                onChange={(e) => setNegForm({ ...negForm, price_cut_pct: e.target.value })}
                className="mt-1.5"
                data-testid="accounts-price-cut"
              />
            </div>
          </div>
          {isPartialUnload && (
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Deduction quantity basis</Label>
              <Select value={deductionScope} onValueChange={setDeductionScope}>
                <SelectTrigger className="mt-1.5 max-w-md" data-testid="accounts-deduction-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPLETE">Complete quantity ({netQtl} qtl)</SelectItem>
                  <SelectItem value="REMAINING">Remaining quantity ({Math.max(netQtl - unloadedQtl, 0).toFixed(2)} qtl)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground">
                Gross amount stays based on final net weight; this option controls deduction calculation basis on partial unload.
              </p>
            </div>
          )}
          <div className="mt-5 flex justify-end">
            <Button
              onClick={accountsFinalize}
              className="bg-bansal-gold hover:bg-bansal-goldDark text-white min-h-[44px]"
              data-testid="accounts-finalize-submit"
            >
              Finalize & generate voucher(s)
            </Button>
          </div>
        </SectionCard>
      )}

      {p.state === "BARGAINING" && (
        <SectionCard title="Bargaining Queue — Decision Required" testId="bargaining-form">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900">
            {p.lab_verdict === "RELAB_REVIEW"
              ? "Relab results are in. Negotiate quality claim — offer cuts or revised rate, or reject."
              : "Lab parameters exceed contract thresholds — awaiting admin verdict. Offer a weight-cut, price-cut, or rate revision, or reject."}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Revised Rate (₹/qtl)</Label>
              <Input type="number" value={negForm.revised_rate} onChange={(e) => setNegForm({ ...negForm, revised_rate: e.target.value })} className="mt-1.5" data-testid="neg-revised-rate" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Weight Cut %</Label>
              <Input type="number" step="0.1" value={negForm.weight_cut_pct} onChange={(e) => setNegForm({ ...negForm, weight_cut_pct: e.target.value })} className="mt-1.5" data-testid="neg-weight-cut" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Price Cut %</Label>
              <Input type="number" step="0.1" value={negForm.price_cut_pct} onChange={(e) => setNegForm({ ...negForm, price_cut_pct: e.target.value })} className="mt-1.5" data-testid="neg-price-cut" />
            </div>
          </div>
          <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
            <Button onClick={() => decide("REJECT")} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 w-full sm:w-auto min-h-[44px]" data-testid="neg-reject">Reject</Button>
            {p.lab_verdict === "RELAB_REVIEW" ? (
              <Button onClick={() => decideResume()} className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto min-h-[44px]" data-testid="neg-resume">Approve Cuts & Resume Unloading</Button>
            ) : (
              <Button onClick={() => decide("APPROVE")} className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto min-h-[44px]" data-testid="neg-approve">Approve with Cuts</Button>
            )}
          </div>
        </SectionCard>
      )}

      {p.unloading && (
        <UnloadingDestinationsSection purchase={p} onReload={load} user={user} />
      )}

      {(p.vouchers?.length > 0 || p.voucher) && (
        <SectionCard title={p.vouchers?.length > 1 ? "Payable vouchers" : "Auto-Generated Voucher"} testId="voucher-summary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <dl className="space-y-2 text-sm">
              <Row label="Voucher"><span className="font-mono text-xs">{p.voucher.code}</span></Row>
              <Row label="Net Weight"><span className="tabular">{p.voucher.net_qtl} qtl</span></Row>
              <Row label="Weight Cut">{p.voucher.weight_cut_pct}%</Row>
              <Row label="Billable Qtl"><span className="tabular">{p.voucher.billable_qtl}</span></Row>
              <Row label="Effective Rate"><span className="tabular">₹{p.voucher.effective_rate}</span></Row>
              <Row label="Gross Amount"><span className="tabular font-semibold">₹{p.voucher.gross_amount.toLocaleString("en-IN")}</span></Row>
              <div className="border-t pt-2 mt-2">
                <Row label={<span className="font-semibold">Net Payable</span>}>
                  <span className="tabular font-serif text-xl text-primary">₹{p.voucher.net_payable.toLocaleString("en-IN")}</span>
                </Row>
              </div>
            </dl>
            <dl className="space-y-2 text-sm bg-muted/70 rounded-md p-4 border border-border">
              <div className="text-[10px] uppercase tracking-widest text-primary font-medium mb-2">Charges added on payable</div>
              {p.party_kind === "SUPPLIER" ? (
                <>
                  {(p.voucher.deductions?.settlement_mode === "SUPPLIER_DIRECT_YARD"
                    || p.effective_landed_rate != null
                    || p.voucher.deductions?.effective_landed_rate != null) && (
                    <div className="rounded border border-emerald-200 bg-emerald-50/50 p-3 mb-2 space-y-1">
                      <p className="text-[10px] uppercase tracking-widest text-primary font-medium">Landed inventory cost</p>
                      <Row label="Base paddy price">
                        <span className="tabular">₹{Number(p.voucher.effective_rate || 0).toLocaleString("en-IN")} / Qtl</span>
                      </Row>
                      <Row label="Supplier commission">
                        <span className="tabular text-primary font-medium">
                          +₹{Number(p.voucher.deductions?.commission_rate ?? p.commission_rate ?? 0).toLocaleString("en-IN")} / Qtl
                        </span>
                      </Row>
                      <Row label="Final landed cost">
                        <span className="tabular font-semibold">
                          ₹{Number(p.voucher.deductions?.effective_landed_rate ?? p.effective_landed_rate ?? 0).toLocaleString("en-IN")} / Qtl
                        </span>
                      </Row>
                    </div>
                  )}
                  {Array.isArray(p.voucher.deductions?.lines) && p.voucher.deductions.lines.length > 0 ? (
                    p.voucher.deductions.lines.map((ln, idx) => (
                      <Row key={idx} label={ln.label || ln.code}>
                        <span className="tabular text-primary font-medium">
                          +₹{Number(ln.amount || 0).toLocaleString("en-IN")}
                          {ln.capitalized_into_inventory ? " (capitalized)" : ""}
                        </span>
                      </Row>
                    ))
                  ) : (
                    <>
                      {p.voucher.deductions?.mandi_cess != null && (
                        <Row label="Mandi Cess"><span className="tabular text-primary font-medium">+₹{Number(p.voucher.deductions.mandi_cess).toLocaleString("en-IN")}</span></Row>
                      )}
                      {p.voucher.deductions?.rdf != null && (
                        <Row label="RDF"><span className="tabular text-primary font-medium">+₹{Number(p.voucher.deductions.rdf).toLocaleString("en-IN")}</span></Row>
                      )}
                      {p.voucher.deductions?.commission != null && (
                        <Row label="Commission"><span className="tabular text-primary font-medium">+₹{Number(p.voucher.deductions.commission).toLocaleString("en-IN")}</span></Row>
                      )}
                    </>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <Row label="Total added on gross">
                      <span className="tabular font-medium text-primary">+₹{Number(p.voucher.deductions?.total ?? 0).toLocaleString("en-IN")}</span>
                    </Row>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                    Net payable = gross amount + these charges (supplier party).
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Itemised supplier charges are shown when this purchase is linked to a <strong>supplier</strong> party at weighbridge.
                  Net payable is shown on the left for all purchases.
                </p>
              )}
            </dl>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function HoldSection({ purchase: p, onReload }) {
  const { user } = useAuth();
  const [holdReason, setHoldReason] = useState("");
  const [holdBags, setHoldBags] = useState("");
  const [relabNotes, setRelabNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const holdUnloading = async () => {
    if (!holdReason.trim()) return toast.error("Enter a hold reason");
    try {
      setBusy(true);
      await api.post(`/purchases/${p.id}/hold`, {
        hold_reason: holdReason,
        hold_bags_count: holdBags ? +holdBags : null,
      });
      toast.success("Unloading put on hold");
      setHoldReason("");
      setHoldBags("");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    try {
      setBusy(true);
      await api.post(`/purchases/${p.id}/resume`);
      toast.success("Unloading resumed");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const requestRelab = async () => {
    try {
      setBusy(true);
      await api.post(`/purchases/${p.id}/request-relab`, { notes: relabNotes });
      toast.success("Relab requested — sent to lab queue");
      setRelabNotes("");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const rejectHeld = async () => {
    try {
      setBusy(true);
      await api.post(`/purchases/transition?purchase_id=${p.id}&to_state=REJECTED`);
      toast.success("Purchase rejected");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  if (p.state === "UNLOADING" && hasPermission(user, "unloading.create")) {
    return (
      <SectionCard title="Hold Unloading" testId="hold-form">
        <p className="text-sm text-muted-foreground mb-3">
          If quality inside the truck is not up to mark, hold unloading and notify the purchase manager.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Hold reason</Label>
            <Textarea value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="Describe the quality issue…" className="mt-1.5" rows={2} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Affected bags (optional)</Label>
            <Input type="number" value={holdBags} onChange={(e) => setHoldBags(e.target.value)} placeholder="e.g. 25" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={holdUnloading} disabled={busy || !holdReason.trim()} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50 min-h-[44px]">
            {busy ? "Holding…" : "Hold Unloading"}
          </Button>
        </div>
      </SectionCard>
    );
  }

  if (p.state === "UNLOADING_HOLD") {
    const isManager = hasRole(user, ["manager", "admin"]);
    return (
      <SectionCard title="Unloading On Hold" testId="hold-decision">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4 text-sm text-amber-900">
          <strong>Hold reason:</strong> {p.hold_reason || "—"}
          {p.hold_bags_count != null && <span className="ml-2">· {p.hold_bags_count} bags affected</span>}
        </div>
        {isManager ? (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Relab notes (optional)</Label>
              <Textarea value={relabNotes} onChange={(e) => setRelabNotes(e.target.value)} placeholder="Notes for relab request…" className="mt-1.5" rows={2} />
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <Button onClick={rejectHeld} disabled={busy} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 min-h-[44px]">Reject Purchase</Button>
              <Button onClick={requestRelab} disabled={busy} variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-50 min-h-[44px]">Request Relab</Button>
              <Button onClick={resume} disabled={busy} className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[44px]">Resume Unloading</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Awaiting manager decision to resume, request relab, or reject.</p>
        )}
      </SectionCard>
    );
  }

  if (p.state === "RELAB_PENDING") {
    return (
      <SectionCard title="Relab Pending" testId="relab-pending">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
          Manager has requested a relab for quality claim negotiation. Awaiting lab results.
          {hasPermission(user, "page.lab") && (
            <span className="ml-2">
              <Link to={labPageUrl("truck")} className="font-medium text-blue-700 hover:underline">Go to Truck Lab →</Link>
            </span>
          )}
        </div>
      </SectionCard>
    );
  }

  return null;
}

function UnloadingDestinationsSection({ purchase: p, onReload, user }) {
  const unloadByQtl = Boolean(p.unload_by_qtl);
  const canEditWeight = hasRole(user, ["admin", "supervisor", "weighbridge"]);
  const segments = p.loose_weigh_segments || [];
  const [weights, setWeights] = useState(() =>
    (p.unloading?.destinations || []).map((d) => (d.weight_qtl != null && d.weight_qtl !== "" ? String(d.weight_qtl) : "")),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setWeights(
      (p.unloading?.destinations || []).map((d) => (d.weight_qtl != null && d.weight_qtl !== "" ? String(d.weight_qtl) : "")),
    );
  }, [p.id, p.unloading?.destinations]);

  const saveWeights = async () => {
    const payload = weights
      .map((w, index) => ({ index, weight_qtl: w === "" ? 0 : parseFloat(w) }))
      .filter((line) => Number.isFinite(line.weight_qtl));
    if (payload.some((line) => line.weight_qtl < 0)) {
      toast.error("Enter valid qtl values");
      return;
    }
    if (unloadByQtl && payload.every((line) => line.weight_qtl <= 0)) {
      toast.error("Enter at least one segment weight (qtl)");
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/unloading/purchase/${p.id}/weights`, { weights: payload });
      toast.success("Destination weights updated");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Unloading Destinations" testId="unload-summary">
      <div className="mb-4 space-y-2 text-sm text-muted-foreground">
        <p>
          <strong>Separate bridge weight (mid)</strong>: location qtl = segment net from that weigh.
          <strong>Complete truck (final tare only)</strong>: location qtl = bags × (net ÷ total bags).
          {unloadByQtl ? " Loose uses bridge segment per unload batch." : " Bagged uses bag counts when no per-location mid."}
          {p.unloading.total_weight_qtl > 0 && (
            <span className="ml-1 font-medium text-foreground tabular">
              Total: {p.unloading.total_weight_qtl} qtl
            </span>
          )}
        </p>
        {segments.length > 0 && (
          <ul className="text-xs space-y-1">
            {segments.map((s, i) => (
              <li key={i} className="tabular">
                <span className="font-medium uppercase">{s.kind}</span> ref {s.reference_qtl} → {s.weight_qtl} qtl
                {" · "}
                <span className="text-primary font-medium">{s.segment_net_qtl} qtl unloaded</span>
                {s.allocation_mode === "bridge_segment" && (
                  <span className="text-muted-foreground"> · full segment → location</span>
                )}
                {s.allocation_mode === "by_bags" && s.weight_per_bag != null && (
                  <span> · {s.weight_per_bag} qtl/bag × {s.batch_bags} bags</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/50">
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Variety</th>
              {!unloadByQtl && <th className="px-3 py-2">Bags</th>}
              <th className="px-3 py-2">Weight (qtl)</th>
            </tr>
          </thead>
          <tbody>
            {(p.unloading.destinations || []).map((d, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-2">
                  <Pill tone={d.location_type === "HOUDI" ? "gold" : "sage"}>{d.location_type}</Pill>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{d.location_id}</td>
                <td className="px-3 py-2">{d.variety}</td>
                {!unloadByQtl && <td className="px-3 py-2 tabular">{d.bags}</td>}
                <td className="px-3 py-2 tabular">
                  {Number(d.weight_qtl) > 0 ? (
                    <span className="font-medium text-primary">{d.weight_qtl} qtl</span>
                  ) : (
                    <span className="text-muted-foreground">Pending weigh</span>
                  )}
                  {canEditWeight && (
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="h-8 w-24 tabular mt-1"
                      value={weights[i] ?? ""}
                      placeholder="Override"
                      onChange={(e) => {
                        const next = [...weights];
                        next[i] = e.target.value;
                        setWeights(next);
                      }}
                      data-testid={`unload-dest-weight-${i}`}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEditWeight && (
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={saveWeights}
            className="min-h-[40px]"
            data-testid="save-unload-weights"
          >
            {saving ? "Saving…" : "Save weight overrides"}
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

